import * as React from 'react';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '../../lib/cn';
import { Tooltip } from '../ui/tooltip';

export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  badge?: React.ReactNode;
  external?: boolean;
  feature?: string;
}

export interface NavSection {
  label?: string;
  items: NavItem[];
}

interface SidebarProps {
  sections: NavSection[];
  logoFull?: React.ReactNode;
  logoCompact?: React.ReactNode;
  footer?: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

function isActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(href + '/');
}

function NavLinks({ sections, collapsed }: { sections: NavSection[]; collapsed: boolean }) {
  const location = useLocation();
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2">
      <ul className="space-y-5">
        {sections.map((section, i) => (
          <li key={i}>
            {section.label && !collapsed && (
              <div className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(location.pathname, item.href);
                const content = (
                  <>
                    <item.icon
                      className={cn(
                        'h-4.5 w-4.5 shrink-0',
                        active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      )}
                      aria-hidden="true"
                      style={{ height: '1.125rem', width: '1.125rem' }}
                    />
                    {!collapsed && <span className="truncate">{item.name}</span>}
                    {!collapsed && item.badge && (
                      <span className="ml-auto">{item.badge}</span>
                    )}
                  </>
                );

                const classes = cn(
                  'group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                  collapsed && 'justify-center px-0 w-9 h-9 mx-auto',
                  active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                );

                const link = item.external ? (
                  <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes}>
                    {content}
                  </a>
                ) : (
                  <Link to={item.href} className={classes}>
                    {active && (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-primary',
                          collapsed && 'left-0'
                        )}
                      />
                    )}
                    {content}
                  </Link>
                );

                return (
                  <li key={item.name}>
                    {collapsed ? (
                      <Tooltip content={item.name} side="right">
                        {link}
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function SidebarInner({
  sections,
  logoFull,
  logoCompact,
  footer,
  collapsed,
  onCollapsedChange,
  showCollapseToggle,
  onCloseMobile,
}: {
  sections: NavSection[];
  logoFull?: React.ReactNode;
  logoCompact?: React.ReactNode;
  footer?: React.ReactNode;
  collapsed: boolean;
  onCollapsedChange?: (c: boolean) => void;
  showCollapseToggle?: boolean;
  // When set (mobile drawer only) renders a close button inside the sidebar's
  // top-right corner so it stays anchored to the visible sidebar regardless
  // of viewport width.
  onCloseMobile?: () => void;
}) {
  return (
    <div className="relative flex h-full w-full flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
      {onCloseMobile && (
        <button
          type="button"
          onClick={onCloseMobile}
          aria-label="Close sidebar"
          className="absolute right-2 top-3 z-10 rounded-md p-2 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      )}
      <div className={cn('flex h-14 shrink-0 items-center border-b border-sidebar-border', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
        {collapsed ? logoCompact : logoFull}
      </div>
      <NavLinks sections={sections} collapsed={collapsed} />
      <div className={cn('shrink-0 border-t border-sidebar-border', collapsed ? 'p-2' : 'p-3 space-y-2')}>
        {showCollapseToggle && onCollapsedChange && (
          <button
            type="button"
            onClick={() => onCollapsedChange(!collapsed)}
            className={cn(
              'hidden lg:inline-flex items-center gap-2 rounded-md text-xs font-medium text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors',
              collapsed ? 'w-9 h-9 justify-center mx-auto' : 'w-full px-2.5 py-1.5 justify-start'
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
            {!collapsed && <span>Collapse</span>}
          </button>
        )}
        {footer && !collapsed && <div className="mt-3 pt-2 border-t border-sidebar-border">{footer}</div>}
      </div>
    </div>
  );
}

export function Sidebar({ sections, logoFull, logoCompact, footer, open, onOpenChange, collapsed, onCollapsedChange }: SidebarProps) {
  return (
    <>
      {/* Mobile */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={onOpenChange}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-250 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-[17rem] flex-1">
                <SidebarInner
                  sections={sections}
                  logoFull={logoFull}
                  logoCompact={logoCompact}
                  footer={footer}
                  collapsed={false}
                  onCloseMobile={() => onOpenChange(false)}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop */}
      <div
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:flex-col transition-[width] duration-200',
          collapsed ? 'lg:w-16' : 'lg:w-64'
        )}
      >
        <SidebarInner
          sections={sections}
          logoFull={logoFull}
          logoCompact={logoCompact}
          footer={footer}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          showCollapseToggle
        />
      </div>
    </>
  );
}
